/**
 * The Care Concierge on/off switch for a firm or a single advisor (Feature 106).
 *
 * Off by default: a firm or advisor is switched on when their arrangement with bQuest is agreed,
 * which happens off the platform, and usually in the same sitting as granting their sessions —
 * hence sitting directly above that section in both editors.
 *
 * Saves on its own rather than with the surrounding form, for the same reason the session grants
 * do: it is an action with an immediate consequence for what an advisor sees, not a draft edit
 * waiting on a Save button somewhere else on the page.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Switch } from '@/shared/components/ui/switch';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/components/Toast';
import {
  getAdvisorConciergeEnabled,
  getPartnerCompanyConciergeEnabled,
  setAdvisorConciergeEnabled,
  setPartnerCompanyConciergeEnabled,
} from '../../services/careConciergeCreditApi';

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
  (error as { message?: string })?.message ??
  fallback;

type CareConciergeToggleProps =
  | { owner: 'partner-company'; ownerId: string }
  | { owner: 'advisor'; ownerId: string };

export const CareConciergeToggle = ({ owner, ownerId }: CareConciergeToggleProps) => {
  const { showToast } = useToast();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState(false);

  const isFirm = owner === 'partner-company';

  useEffect(() => {
    let active = true;

    const read = isFirm ? getPartnerCompanyConciergeEnabled : getAdvisorConciergeEnabled;
    read(ownerId)
      .then((result) => {
        if (!active) return;
        setEnabled(result.enabled);
        setLoadError(null);
      })
      .catch((error) => {
        // Recorded rather than only announced: a toast disappears and would leave the admin
        // staring at "Loading…" with nothing to click.
        if (active) setLoadError(errorMessage(error, 'Could not read the Care Concierge switch.'));
      });

    // Guards against a late response from a previous owner landing on the current one.
    return () => {
      active = false;
    };
  }, [isFirm, ownerId, reloadToken]);

  const handleChange = async (next: boolean) => {
    setBusy(true);
    try {
      const write = isFirm ? setPartnerCompanyConciergeEnabled : setAdvisorConciergeEnabled;
      const result = await write(ownerId, next);
      setEnabled(result.enabled);
      showToast(
        result.enabled
          ? 'Care Concierge consultations are now available.'
          : 'Care Concierge consultations are switched off.',
        'success',
      );
    } catch (error) {
      showToast(errorMessage(error, 'Could not save that change.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="mb-4 rounded-md border border-destructive/40 p-3">
        <p className="text-sm text-destructive">{loadError}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (enabled === null) {
    return <p className="text-sm text-muted-foreground">Loading the Care Concierge switch…</p>;
  }

  return (
    <div className="mb-4 flex items-start justify-between gap-4 rounded-md border border-border p-3">
      <div>
        <Label htmlFor="cc-enabled">Offer Care Concierge consultations</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          {isFirm
            ? enabled
              ? 'Advisors at this firm can arrange consultations, unless switched off individually.'
              : 'No advisor at this firm can arrange consultations, whatever their own setting says.'
            : enabled
              ? 'This advisor can arrange consultations, as long as their firm is switched on too.'
              : 'This advisor cannot arrange consultations. Cases they already have stay visible to them.'}
        </p>
      </div>
      <Switch
        id="cc-enabled"
        checked={enabled}
        onCheckedChange={(value) => void handleChange(value)}
        disabled={busy}
      />
    </div>
  );
};
